/** Wiki Discovery page — browse by category, tag, vendor, new/recent entries. */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../auth/api';
import { FolderOpen, Tag, Building2, Sparkles, Clock } from 'lucide-react';

export default function WikiDiscovery() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get('/wiki/discover');
                setData(res.data);
            } catch (e) {
                setError('Failed to load discovery page');
            }
            setLoading(false);
        };
        load();
    }, []);

    if (loading) return <div className="max-w-6xl mx-auto p-6 text-slate-400">Loading discovery page...</div>;
    if (error) return <div className="max-w-6xl mx-auto p-6 text-red-400">{error}</div>;
    if (!data) return null;

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-8">
            <div>
                <h1 className="text-2xl font-bold text-white mb-1">Discover LEXIS Wiki</h1>
                <p className="text-slate-400 text-sm">Browse intelligence by category, tag, or vendor.</p>
            </div>

            {/* Categories */}
            <Section icon={<FolderOpen size={18} />} title="Categories">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {data.categories.map(cat => (
                        <Link
                            key={cat.id}
                            to={`/wiki?category=${cat.name}`}
                            className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/30 transition-colors group"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">{cat.icon || '📁'}</span>
                                <span className="text-sm font-medium text-white group-hover:text-amber-400 transition-colors truncate">
                                    {cat.name}
                                </span>
                            </div>
                            <div className="text-xs text-slate-400">{cat.entry_count} entries</div>
                        </Link>
                    ))}
                </div>
            </Section>

            {/* Trending Tags */}
            {data.trending_tags.length > 0 && (
                <Section icon={<Tag size={18} />} title="Trending Tags (30 days)">
                    <div className="flex flex-wrap gap-2">
                        {data.trending_tags.map(tag => (
                            <Link
                                key={tag.id}
                                to={`/wiki?tag=${tag.slug}`}
                                className="px-3 py-1.5 rounded-full text-sm font-medium border transition-colors hover:shadow-md"
                                style={{
                                    background: `${tag.color}22`,
                                    borderColor: `${tag.color}55`,
                                    color: tag.color,
                                }}
                            >
                                {tag.display_name}
                                <span className="ml-1.5 opacity-60">{tag.entry_count}</span>
                            </Link>
                        ))}
                    </div>
                </Section>
            )}

            {/* Top Vendors */}
            {data.top_vendors.length > 0 && (
                <Section icon={<Building2 size={18} />} title="Top Vendors">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {data.top_vendors.map(vendor => (
                            <Link
                                key={vendor.name}
                                to={`/wiki?vendor=${vendor.name}`}
                                className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/30 transition-colors"
                            >
                                <div className="text-sm font-medium text-white">{vendor.name}</div>
                                <div className="text-xs text-slate-400">
                                    {vendor.country || 'Unknown'} &middot; {vendor.entry_count} entries
                                </div>
                            </Link>
                        ))}
                    </div>
                </Section>
            )}

            {/* New & Recent */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Section icon={<Sparkles size={18} />} title="New Entries (14 days)">
                    {data.new_entries.length === 0 ? (
                        <div className="text-slate-400 text-sm">No new entries in the last 14 days.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.new_entries.map(e => (
                                <EntryItem key={e.id} entry={e} />
                            ))}
                        </div>
                    )}
                </Section>

                <Section icon={<Clock size={18} />} title="Recently Updated (7 days)">
                    {data.recently_updated.length === 0 ? (
                        <div className="text-slate-400 text-sm">No recent updates.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.recently_updated.map(e => (
                                <EntryItem key={e.id} entry={e} />
                            ))}
                        </div>
                    )}
                </Section>
            </div>
        </div>
    );
}

function Section({ icon, title, children }) {
    return (
        <div>
            <h2 className="text-base font-semibold text-amber-400 mb-3 flex items-center gap-2">
                {icon} {title}
            </h2>
            {children}
        </div>
    );
}

function EntryItem({ entry }) {
    const fmtDate = (iso) => {
        const d = new Date(iso);
        return d.toLocaleDateString('en-GB');
    };

    return (
        <Link to={`/wiki/entry/${entry.slug}`} className="flex items-start gap-3 p-2 rounded hover:bg-slate-800/50 transition-colors group">
            <div className="w-1 h-1 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm text-white group-hover:text-amber-400 transition-colors truncate">
                    {entry.title_en || entry.title_vi}
                </div>
                <div className="text-xs text-slate-400">
                    {entry.category?.name && <span className="mr-2">{entry.category.name}</span>}
                    <span>{fmtDate(entry.created_at || entry.updated_at)}</span>
                </div>
            </div>
        </Link>
    );
}